import {
  isValidTelHref,
  looksLikeTelHref,
  normalizeTelHref,
} from "@/utility/validateTelHref";
import { showTelConfirmModal } from "@/assets/utilities/tinymce/helpers/telConfirmModal";
import { isLinkClassChipCommit } from "@/assets/utilities/tinymce/helpers/linkDialogFields";
import i18n from "@/i18n";

// TinyMCE removes <a> tags with empty href — space prevents that
const EMPTY_HREF_PLACEHOLDER = " ";

const CONFIRM_MESSAGE =
  "The phone link does not match the expected format (tel:+12345678910). Are you sure you want to save it?";

const findDialogUrlInput = container => {
  return (
    container.querySelector('input.tox-textfield[aria-label="URL"]') ||
    container.querySelector('input.tox-textfield[placeholder*="http"]') ||
    container.querySelector('input.tox-textfield[placeholder*="tel:"]') ||
    container.querySelector("input.tox-textfield")
  );
};

/**
 * Insert dialog: blocks save with error (no confirm).
 * Inline edit (Enter / blur): blocks, shows confirm modal, applies or reverts.
 */
export function installTelHrefGuards(editor) {
  let removeTelDialogGuards = null;
  let linkAdjacentTelErrorEl = null;
  let removeTelInlineToolbarGuards = null;
  let activeInlineLinkEl = null;
  let activeInlineLinkPreviousHref = "";
  // Tracks whether a confirm modal is currently open to prevent duplicates
  let confirmModalOpen = false;

  const clearLinkAdjacentTelError = () => {
    try {
      if (linkAdjacentTelErrorEl?.remove) linkAdjacentTelErrorEl.remove();
    } catch (_) {
      // ignore
    }
    linkAdjacentTelErrorEl = null;
  };

  const upsertLinkAdjacentTelError = (linkEl, message) => {
    if (!linkEl) return;

    const inputField = document.querySelector(".tox-toolbar-textfield");
    const toolbarContainer =
      inputField?.closest(".tox-pop__dialog") || inputField?.closest(".tox-pop") || inputField?.parentElement;
    if (!toolbarContainer) return;

    if (linkAdjacentTelErrorEl && !linkAdjacentTelErrorEl.isConnected) {
      linkAdjacentTelErrorEl = null;
    }

    if (!linkAdjacentTelErrorEl) {
      const el = document.createElement("div");
      el.className = "sr-tel-href-link-error";
      el.style.cssText =
        "max-width:100%;width:100%;box-sizing:border-box;" +
        "padding:4px 10px;color:#dc3545;font-size:12px;font-weight:600;" +
        "line-height:1.25;word-break:break-word;";

      const msg = document.createElement("span");
      msg.className = "sr-tel-href-link-error__text";
      msg.style.cssText = "display:inline;white-space:normal;";
      el.appendChild(msg);

      toolbarContainer.appendChild(el);
      linkAdjacentTelErrorEl = el;
    }

    const textEl = linkAdjacentTelErrorEl.querySelector(".sr-tel-href-link-error__text");
    if (textEl) textEl.textContent = message;
  };

  const upsertDialogTelError = (dialogEl, message) => {
    if (!dialogEl) return;
    const existing = dialogEl.querySelector(".sr-tel-href-error");
    if (existing) {
      existing.textContent = message;
      return;
    }

    const el = document.createElement("div");
    el.className = "sr-tel-href-error";
    el.textContent = message;
    el.style.cssText =
      "display:block;margin-top:0px;margin-bottom:12px;padding:12px 14px;" +
      "border-radius:6px;border:1px solid rgba(220,53,69,0.75);" +
      "background:rgba(220,53,69,0.12);color:#dc3545;" +
      "font-size:12px;font-weight:600;line-height:1.35;word-break:break-word;";

    const urlInput = findDialogUrlInput(dialogEl);
    const group = urlInput?.closest?.(".tox-form__group") || urlInput?.parentElement || null;
    if (group?.parentElement) {
      group.parentElement.insertBefore(el, group.nextSibling);
      return;
    }

    const body = dialogEl.querySelector(".tox-dialog__body-content") || dialogEl;
    body.appendChild(el);
  };

  const clearDialogTelError = dialogEl => {
    const existing = dialogEl?.querySelector?.(".sr-tel-href-error");
    if (existing) existing.remove();
  };

  // Dialog: blocks save with error, no confirm
  const validateAndNormalizeDialogTelInput = dialog => {
    const urlInput = findDialogUrlInput(dialog);
    if (!urlInput) return { blocked: false };

    const raw = String(urlInput.value ?? "");
    const trimmed = raw.trim();
    if (!looksLikeTelHref(trimmed)) {
      clearDialogTelError(dialog);
      return { blocked: false };
    }

    const normalized = normalizeTelHref(trimmed);
    if (normalized !== trimmed) urlInput.value = normalized;

    if (isValidTelHref(normalized)) {
      clearDialogTelError(dialog);
      return { blocked: false };
    }

    upsertDialogTelError(dialog, i18n.t("shortcodes.live_chat.invalid_tel_href_inline", { href: normalized }));
    urlInput.focus();
    return { blocked: true };
  };

  // Inline edit: blocks, shows confirm modal, applies or reverts
  const showInlineConfirm = (linkEl, normalized) => {
    if (confirmModalOpen) return;
    confirmModalOpen = true;

    const originalHref = activeInlineLinkPreviousHref || "";

    showTelConfirmModal(CONFIRM_MESSAGE, normalized).then(confirmed => {
      confirmModalOpen = false;

      if (confirmed) {
        editor.dom.setAttrib(linkEl, "href", normalized || EMPTY_HREF_PLACEHOLDER);
        clearLinkAdjacentTelError();
        activeInlineLinkEl = null;
        activeInlineLinkPreviousHref = "";
        editor.focus();
        // Move cursor to end of the link, same as normal Enter save behavior
        editor.selection.select(linkEl);
        editor.selection.collapse(false);
      } else {
        editor.dom.setAttrib(linkEl, "href", originalHref || EMPTY_HREF_PLACEHOLDER);
        clearLinkAdjacentTelError();
        // Re-focus the inline toolbar input so user can fix the value
        const inputField = document.querySelector(".tox-toolbar-textfield");
        if (inputField) {
          inputField.value = originalHref;
          inputField.focus();
        }
      }
    });
  };

  const installTelInlineToolbarGuards = () => {
    const onKeydownCapture = e => {
      if (e.key !== "Enter" && e.key !== "Escape") return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.classList?.contains("tox-toolbar-textfield")) return;

      const editorContainer = editor.getContainer?.();
      if (editorContainer && !editorContainer.contains(target) && !target.closest(".tox-tinymce-aux")) return;

      const selectedNode = editor.selection?.getNode?.();
      const linkEl =
        activeInlineLinkEl || selectedNode?.closest?.("a") || (selectedNode?.nodeName === "A" ? selectedNode : null);
      if (!linkEl) return;

      const value = String(target.value ?? "").trim();

      if (e.key === "Escape") {
        if (looksLikeTelHref(value)) {
          const normalized = normalizeTelHref(value);
          if (!isValidTelHref(normalized)) {
            const originalHref = String(target.dataset?.srTelPrevHref ?? activeInlineLinkPreviousHref ?? "");
            editor.dom.setAttrib(linkEl, "href", originalHref || EMPTY_HREF_PLACEHOLDER);
            target.value = originalHref;
            clearLinkAdjacentTelError();
            activeInlineLinkEl = null;
            activeInlineLinkPreviousHref = "";
            delete target.dataset.srTelPrevHref;
          }
        }
        return;
      }

      if (!looksLikeTelHref(value)) {
        clearLinkAdjacentTelError();
        return;
      }

      const normalized = normalizeTelHref(value);
      if (isValidTelHref(normalized)) {
        clearLinkAdjacentTelError();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      upsertLinkAdjacentTelError(linkEl, i18n.t("shortcodes.live_chat.invalid_tel_href_inline", { href: normalized }));
      showInlineConfirm(linkEl, normalized);
    };

    document.addEventListener("keydown", onKeydownCapture, true);
    return () => document.removeEventListener("keydown", onKeydownCapture, true);
  };

  const installTelLinkDialogGuards = () => {
    const onKeydownCapture = e => {
      if (e.key !== "Enter") return;
      if (isLinkClassChipCommit(e.target)) return;
      const dialog = e.target?.closest?.(".tox-dialog");
      if (!dialog) return;

      const { blocked } = validateAndNormalizeDialogTelInput(dialog);
      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    const onClickCapture = e => {
      const btn = e.target?.closest?.("button.tox-button:not(.tox-button--secondary):not(.tox-button--icon)");
      if (!btn) return;
      const dialog = btn.closest(".tox-dialog");
      if (!dialog) return;

      const { blocked } = validateAndNormalizeDialogTelInput(dialog);
      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };

    document.addEventListener("keydown", onKeydownCapture, true);
    document.addEventListener("click", onClickCapture, true);

    return () => {
      document.removeEventListener("keydown", onKeydownCapture, true);
      document.removeEventListener("click", onClickCapture, true);
    };
  };

  removeTelDialogGuards = installTelLinkDialogGuards();

  editor.on("BeforeExecCommand", e => {
    if (e.command !== "mceInsertLink") return;
    if (confirmModalOpen) return;

    const rawHref =
      typeof e.value === "string"
        ? e.value
        : e.value && typeof e.value === "object" && typeof e.value.href === "string"
          ? e.value.href
          : "";
    const trimmed = rawHref.trim();
    if (!looksLikeTelHref(trimmed)) return;

    const normalized = normalizeTelHref(trimmed);
    if (normalized !== trimmed) {
      if (typeof e.value === "string") {
        e.value = normalized;
      } else if (e.value && typeof e.value === "object") {
        e.value.href = normalized;
      }
    }

    if (isValidTelHref(normalized)) return;

    e.preventDefault();
  });

  editor.on("remove", () => {
    clearLinkAdjacentTelError();
    if (removeTelDialogGuards) removeTelDialogGuards();
    removeTelDialogGuards = null;
    if (removeTelInlineToolbarGuards) removeTelInlineToolbarGuards();
    removeTelInlineToolbarGuards = null;
  });

  const onInit = () => {
    if (!removeTelInlineToolbarGuards) {
      removeTelInlineToolbarGuards = installTelInlineToolbarGuards();
    }
  };

  const onNodeChange = e => {
    if (!document.querySelector(".tox-toolbar-textfield")) {
      clearLinkAdjacentTelError();
      activeInlineLinkEl = null;
      activeInlineLinkPreviousHref = "";
    }

    if (e.element.nodeName === "A" || e?.parents?.some(el => el?.nodeName === "A")) {
      setTimeout(() => {
        const el = e.element.nodeName === "A" ? e.element : e.parents.find(p => p.nodeName === "A");
        const inputField = document.querySelector(".tox-toolbar-textfield");

        if (inputField) {
          const currentHref = editor.dom.getAttrib(el, "href") || "";
          if (activeInlineLinkEl !== el) {
            activeInlineLinkEl = el;
            activeInlineLinkPreviousHref = currentHref;
          }
          const originalHref = activeInlineLinkPreviousHref || "";
          inputField.dataset.srTelPrevHref = originalHref;

          const normalizeAndValidateInput = input => {
            const raw = String(input.value ?? "");
            const normalized = normalizeTelHref(raw);
            if (normalized !== raw) input.value = normalized;
            const valid = looksLikeTelHref(normalized) ? isValidTelHref(normalized) : null;
            return { normalized, valid };
          };

          inputField.oninput = event => {
            const { normalized, valid } = normalizeAndValidateInput(event.target);

            const nextHref = normalized || EMPTY_HREF_PLACEHOLDER;
            editor.dom.setAttrib(el, "href", nextHref);

            if (valid === null || valid) {
              clearLinkAdjacentTelError();
            } else {
              upsertLinkAdjacentTelError(el, i18n.t("shortcodes.live_chat.invalid_tel_href_inline", { href: nextHref }));
            }
          };

          inputField.onblur = event => {
            const { normalized, valid } = normalizeAndValidateInput(event.target);

            if (valid === null || valid) {
              clearLinkAdjacentTelError();
              activeInlineLinkEl = null;
              activeInlineLinkPreviousHref = "";
              delete event.target.dataset.srTelPrevHref;
              return;
            }

            showInlineConfirm(el, normalized);
          };
        }
      }, 100);
    }
  };

  return { onInit, onNodeChange };
}
